// Экран входа/регистрации (SPEC §4.6, FR-AUTH). Вход: email+password → session.login (cookie ставит
// браузер, D-07). Регистрация: name/email(корп-домен)/password/должность?/отдел? → rest.register →
// pending-экран. Используется и как стартовый экран (нет сессии), и как МОДАЛЬНЫЙ релогин (поверх, при
// auth.required — кеш/маршрут сохранены). Сетевое — через transport.rest.

import { useCallback, useState, type CSSProperties, type FormEvent } from 'react'
import { Button } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { useSession } from '../auth/SessionContext.js'
import { strings } from '../i18n/strings.js'
import { registerErrorText } from '../i18n/apiErrors.js'

export interface AuthScreenProps {
  /** Релогин-режим: рисуем как модальный оверлей (кеш/маршрут целы, §4.6). */
  relogin?: boolean
}

type Mode = 'signin' | 'signup' | 'pending'

export function AuthScreen({ relogin = false }: AuthScreenProps): JSX.Element {
  const rest = useRest()
  const { login } = useSession()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [department, setDepartment] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault()
      setError(null)
      setBusy(true)
      try {
        if (mode === 'signin') {
          await login(email, password)
        } else {
          await rest.register({
            name,
            email,
            password,
            title: title.length > 0 ? title : undefined,
            department: department.length > 0 ? department : undefined,
          })
          setMode('pending')
        }
      } catch (err) {
        if (mode === 'signin') {
          // FORBIDDEN на входе = пароль верный, но аккаунт не одобрен/деактивирован (см. auth-роут).
          const code = (err as { code?: string } | null)?.code
          setError(code === 'FORBIDDEN' ? strings.auth.pendingApproval : strings.auth.invalidCreds)
        } else {
          // Причина отказа регистрации — по коду/тексту конверта (короткий пароль/домен/занятый email):
          // generic здесь заставлял пользователей вслепую повторять попытки (журналы 02.07).
          setError(registerErrorText(err))
        }
      } finally {
        setBusy(false)
      }
    },
    [mode, login, rest, email, password, name, title, department],
  )

  const wrap: CSSProperties = relogin
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-sheet)' as unknown as number,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    : {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
  const card: CSSProperties = {
    width: 'min(92vw, 380px)',
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-card)',
    padding: 'var(--space-5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
  }
  const input: CSSProperties = {
    width: '100%',
    padding: '10px var(--space-3)',
    borderRadius: 'var(--radius-control)',
    border: '1px solid var(--color-divider)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-md)',
  }

  if (mode === 'pending') {
    return (
      <div data-screen="auth" data-mode="pending" style={wrap}>
        <div style={card}>
          <h2 style={{ margin: 0 }}>{strings.auth.pendingTitle}</h2>
          <p style={{ color: 'var(--color-text-muted)' }}>{strings.auth.pendingBody}</p>
          <Button variant="ghost" onClick={() => setMode('signin')}>
            {strings.auth.toSignIn}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div data-screen="auth" data-mode={mode} data-relogin={relogin ? '' : undefined} style={wrap}>
      <form style={card} onSubmit={(e) => void submit(e)}>
        <h2 style={{ margin: 0 }}>
          {relogin ? strings.auth.reloginTitle : mode === 'signin' ? strings.auth.signIn : strings.auth.signUp}
        </h2>
        {relogin ? (
          <p style={{ color: 'var(--color-text-muted)', margin: 0, fontSize: 'var(--font-sm)' }}>
            {strings.auth.reloginBody}
          </p>
        ) : null}

        {mode === 'signup' ? (
          <input data-field="name" style={input} placeholder={strings.auth.name} value={name} onChange={(e) => setName(e.target.value)} required />
        ) : null}
        <input
          data-field="email"
          type="email"
          style={input}
          placeholder={strings.auth.email}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          data-field="password"
          type="password"
          style={input}
          placeholder={strings.auth.password}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {mode === 'signup' ? (
          <>
            <input data-field="title" style={input} placeholder={strings.auth.title} value={title} onChange={(e) => setTitle(e.target.value)} />
            <input data-field="department" style={input} placeholder={strings.auth.department} value={department} onChange={(e) => setDepartment(e.target.value)} />
            <span style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
              {strings.auth.corpDomainHint}. {strings.auth.passwordHint}.
            </span>
          </>
        ) : null}

        {error !== null ? (
          <div data-auth-error="" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-sm)' }}>
            {error}
          </div>
        ) : null}

        <Button type="submit" variant="primary" disabled={busy} data-submit="">
          {mode === 'signin' ? strings.auth.submitSignIn : strings.auth.submitSignUp}
        </Button>

        {!relogin ? (
          <Button
            type="button"
            variant="ghost"
            data-switch-mode=""
            onClick={() => {
              setError(null)
              setMode((m) => (m === 'signin' ? 'signup' : 'signin'))
            }}
          >
            {mode === 'signin' ? strings.auth.toSignUp : strings.auth.toSignIn}
          </Button>
        ) : null}
      </form>
    </div>
  )
}
